import React from 'react';
import { flushSync } from 'react-dom';
import { useTheme } from '@/contexts/ThemeContext';
import ObliqueIcon from '@/components/icons/ObliqueIcon';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * Oblique Design System — ThemeToggle
 * Universal Theme Toggle
 * Supports "Prestige" Circular Reveal Animation.
 * Uses local Lottie assets for a premium feel.
 * ═══════════════════════════════════════════════════════════════════════════
 */
export function ThemeToggle({ style = {}, floating = true, className = '' }) {
    const { theme, toggleTheme } = useTheme();

    const handleToggle = (e) => {
        // Prestige Circular Reveal Animation
        if (!document.startViewTransition) {
            toggleTheme();
            return;
        }

        const rect = e.currentTarget.getBoundingClientRect();
        // Force exactly the center of the icon for the prestige reveal
        const x = rect.left + rect.width / 2;
        const y = rect.top + rect.height / 2;

        const endRadius = Math.hypot(
            Math.max(x, window.innerWidth - x),
            Math.max(y, window.innerHeight - y)
        );

        const transition = document.startViewTransition(() => {
            flushSync(() => {
                toggleTheme();
            });
        });

        transition.ready.then(() => {
            const clipPath = [
                `circle(0px at ${x}px ${y}px)`,
                `circle(${endRadius}px at ${x}px ${y}px)`,
            ];

            document.documentElement.animate(
                {
                    clipPath: clipPath,
                },
                {
                    duration: 600, // Smoother timing
                    easing: 'cubic-bezier(0.4, 0, 0.2, 1)', // Flawless Prestige curve
                    pseudoElement: '::view-transition-new(root)',
                }
            );
        });
    };

    const isDark = theme === 'dark';

    return (
        <button
            id="global-theme-toggle"
            onClick={handleToggle}
            aria-label={`Switch to ${isDark ? 'light' : 'dark'} mode`}
            style={{
                width: floating ? '44px' : '34px',
                height: floating ? '44px' : '34px',
                borderRadius: floating ? '50%' : '8px',
                background: floating 
                    ? (isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)')
                    : 'transparent',
                border: floating 
                    ? `1px solid ${isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.1)'}`
                    : '1px solid transparent',
                backdropFilter: floating ? 'blur(10px)' : 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: floating ? 'fixed' : 'relative',
                bottom: floating ? '24px' : 'auto',
                left: floating ? '24px' : 'auto',
                zIndex: 1000,
                overflow: 'hidden',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                outline: 'none',
                boxShadow: 'none',
                padding: 0,
                color: 'var(--text-secondary)',
                viewTransitionName: 'theme-toggle-btn',
                ...style
            }}
            className={className}
        >
            <div style={{
                position: 'relative',
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }}>
                {/* Sun Logic (Clear Day) */}
                <div style={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                    transform: !isDark ? 'scale(1) rotate(0deg)' : 'scale(0.5) rotate(-90deg)',
                    opacity: !isDark ? 1 : 0,
                    pointerEvents: !isDark ? 'auto' : 'none',
                    zIndex: !isDark ? 2 : 1
                }}>
                    <ObliqueIcon 
                        name="light_mode" 
                        size={floating ? 26 : 22} 
                        color="var(--text-secondary)" 
                    />
                </div>

                {/* Moon Logic (Bedtime) */}
                <div style={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                    transform: isDark ? 'scale(1.1) rotate(0deg)' : 'scale(0.5) rotate(90deg)',
                    opacity: isDark ? 1 : 0,
                    pointerEvents: isDark ? 'auto' : 'none',
                    zIndex: isDark ? 2 : 1
                }}>
                    <ObliqueIcon 
                        name="dark_mode" 
                        size={floating ? 26 : 22} 
                        color="var(--text-secondary)" 
                    />
                </div>
            </div>
        </button>
    );
}
